import { Link } from 'react-router-dom';
import { ThemeToggle } from './theme-toggle';
import { cn } from '@/lib/utils';
import logo from '@/assets/logo.png';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { Button } from '@/components/ui/button';
import { LogOut, Menu } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchUserAttributes } from 'aws-amplify/auth';

interface NavbarProps {
  onMenuClick?: () => void;
  showMenuButton?: boolean;
}

export function Navbar({ onMenuClick, showMenuButton = false }: NavbarProps) {
  const { isAuthenticated, logout, user } = useCognitoAuth();
  const [userName, setUserName] = useState<string | null>(null);

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchUserAttributes()
        .then((attributes) => {
          const name = attributes.name || attributes.given_name || user.username || null;
          setUserName(name);
        })
        .catch(() => {
          // Fallback to username if attributes can't be fetched
          setUserName(user.username || null);
        });
    } else {
      setUserName(null);
    }
  }, [isAuthenticated, user]);

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header
      className={cn(
        'sticky top-0 z-40 flex h-14 items-center gap-4 border-b bg-background px-4',
        'sm:px-6',
      )}
    >
      <div className="flex items-center gap-3">
        {showMenuButton && (
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onMenuClick}
            aria-label="Open menu"
          >
            <Menu className="h-5 w-5" />
          </Button>
        )}
        <Link to="/" className="flex items-center gap-2">
          <img src={logo} alt="Margembie Labradors" className="h-8 w-auto" />
          <span className="font-semibold tracking-tight hidden sm:inline-block">
            Margembie Admin
          </span>
        </Link>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {isAuthenticated && userName && (
          <span className="text-sm text-muted-foreground hidden sm:inline-block">
            Hi {userName}
          </span>
        )}
        <ThemeToggle />
        {isAuthenticated && (
          <Button variant="ghost" size="icon" onClick={handleSignOut} aria-label="Sign out">
            <LogOut className="h-5 w-5" />
          </Button>
        )}
      </div>
    </header>
  );
}
